import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Button, Input } from '../../../ui';

interface TimeEntryFormProps {
  initialTime?: Date;
  onSubmit: (time: Date, taskName?: string, notes?: string) => void;
  buttonLabel?: string;
  isCompact?: boolean;
  isSubmitting?: boolean;
}

export default function TimeEntryForm({
  initialTime = new Date(),
  onSubmit,
  buttonLabel = 'Clock In',
  isCompact = false,
  isSubmitting = false,
}: TimeEntryFormProps) {
  const [time, setTime] = useState(initialTime);
  const [taskName, setTaskName] = useState('');
  const [notes, setNotes] = useState('');
  const [taskNameError, setTaskNameError] = useState<string | null>(null);

  // Format date for datetime-local input
  const formatDatetimeLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  };

  const handleSubmit = () => {
    // Validate task name for clock in
    if (buttonLabel === 'Clock In' && !taskName.trim()) {
      setTaskNameError('Task name is required');
      return;
    }

    setTaskNameError(null);
    onSubmit(time, taskName, notes);
  };

  if (isCompact) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <input
          type="datetime-local"
          className="ck-date-input"
          style={{ width: 'auto' }}
          value={formatDatetimeLocal(time)}
          onChange={(e) => {
            const newDate = new Date(e.target.value);
            if (!isNaN(newDate.getTime())) {
              setTime(newDate);
            }
          }}
        />
        <Button size="sm" variant="secondary" onClick={handleSubmit} disabled={isSubmitting}>
          {buttonLabel}
        </Button>
      </div>
    );
  }

  return (
    <div
      style={{
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-hairline)',
        borderRadius: 'var(--radius-md)',
        padding: '16px',
      }}
    >
      <div className="t-eyebrow" style={{ marginBottom: '14px' }}>Begin Time Tracking</div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <div>
          <Input
            label="Task Name"
            value={taskName}
            onChange={(e) => {
              setTaskName(e.target.value);
              if (e.target.value.trim()) {
                setTaskNameError(null);
              }
            }}
            placeholder="What are you working on?"
          />
          {taskNameError && (
            <p style={{ margin: '6px 0 0', fontSize: 'var(--text-xs)', color: 'var(--danger-400)' }}>
              {taskNameError}
            </p>
          )}
        </div>

        <div>
          <div className="t-eyebrow" style={{ marginBottom: '7px' }}>Start Time</div>
          <input
            type="datetime-local"
            value={formatDatetimeLocal(time)}
            onChange={(e) => {
              const newDate = new Date(e.target.value);
              if (!isNaN(newDate.getTime())) {
                setTime(newDate);
              }
            }}
            className="ck-date-input"
          />
        </div>

        <div>
          <div className="t-eyebrow" style={{ marginBottom: '7px' }}>Notes</div>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Optional notes about this time entry..."
            className="ck-textarea"
          />
        </div>

        <Button onClick={handleSubmit} disabled={isSubmitting} style={{ width: '100%' }}>
          <FontAwesomeIcon
            icon={isSubmitting ? faSpinner : faPlay}
            className={isSubmitting ? 'animate-spin' : undefined}
            style={{ fontSize: '11px', marginRight: '8px' }}
          />
          {isSubmitting ? 'Clocking In...' : 'Clock In'}
        </Button>
      </div>
    </div>
  );
}
